import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconMicrophone, IconPaperclip, IconSearch } from "@tabler/icons-react";
import { AnnouncementFeature, AnnouncementShell } from "./AnnouncementShell";
import { heroFor, type AnnouncementProps, type LocalizedHero } from "./types";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import heroEn from "../../assets/images/announcements/juisci-gpt.en.webp";
import heroFr from "../../assets/images/announcements/juisci-gpt.fr.webp";

/**
 * The hero is a screenshot of the chat input, so it carries the placeholder
 * sentence: "Describe a case, a decision, or a clinical doubt" in English,
 * "Décrivez un cas, une décision ou un doute clinique" in French. A language
 * with no artwork of its own falls back to English.
 */
const HERO: LocalizedHero = {
  en: heroEn,
  fr: heroFr,
  it: null,
  de: null,
  es: null,
  pt: null,
  ar: null,
  nl: null,
};

/**
 * "Answers you can source, weigh, and challenge." — Figma 6686:7384, with the
 * French frame at 7464:7287.
 *
 * The copy is the frame's, and it stays in `t()` rather than in the database: an
 * announcement is code, so its words travel with the app's own locale files like
 * every other string on screen. The artwork travels with it for the same reason,
 * bundled rather than fetched from a bucket: a hero that 404s is a broken
 * announcement nobody notices until it has already interrupted someone.
 */
export function JuisciGptAnnouncement({
  isOpen,
  onDismiss,
  onAct,
}: AnnouncementProps): React.JSX.Element {
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();
  const history = useHistory();

  return (
    <AnnouncementShell
      presentation="sheet"
      maxWidth="wide"
      isOpen={isOpen}
      onDismiss={onDismiss}
      media={
        <img
          src={heroFor(HERO, activeLang)}
          alt=""
          className="block h-68 w-full object-cover"
        />
      }
      label={t("New")}
      title={t("Answers you can source, weigh, and challenge.")}
      actions={[
        {
          label: t("Ask a question"),
          variant: "primary",
          onPress: () => {
            onAct("explore-gpt");
            history.push("/gpt/chat/new");
          },
        },
      ]}
      features={
        <>
          <AnnouncementFeature
            icon={<IconSearch size={17} stroke={1.5} />}
            title={t("Looks it up, every time")}
            description={t(
              "Every question runs a fresh search across recent publications and society guidelines, not a recap from memory."
            )}
          />
          <AnnouncementFeature
            icon={<IconPaperclip size={17} stroke={1.5} />}
            title={t("Trace every answer to its sources")}
            description={t(
              "Each answer is built from the publications it cites. Open them and judge for yourself. If Juisci finds nothing, it says so."
            )}
          />
          <AnnouncementFeature
            icon={<IconMicrophone size={17} stroke={1.5} />}
            title={t("Ask it the way you'd ask a colleague")}
            description={t(
              "Dictate or type a full clinical description. Juisci analyses it and shows you what the literature says. Skip patient identifiers."
            )}
          />
        </>
      }
    />
  );
}
